import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { DiskService } from '../disk/disk.service';
import { DeltaService, EntityNames } from '@omni/data-services/delta/delta.service';
import { DateTimeFormatsService } from '../date-time-formats/date-time-formats.service';
import { AuthenticationService } from '../authentication.service';
import { OfflineWritableServiceBase, PushOutcome } from '../offline/offline.service';
import { DB_KEY_PREFIXES, OFFLINE_DATA_COUNT_ENTITY_NAME, PREFIX_SEARCH_ENDKEY_UNICODE } from '@omni/config/pouch-db.config';
import { FeatureActionsMap } from '@omni/classes/authentication/user.class';
import { CustomerType, RawVoiceNote, VoiceNote, VoiceNoteType, deriveNoteTitle } from '@omni/classes/voice-note/voice-note.class';
import { DetectedMentions } from '@omni/ai/features/detected-mentions';
import { CapturedAudio } from '@omni/services/speech/audio-recorder';
import { RecordingStore } from '@omni/services/speech/recording-store';
import { DynamicsClientService } from '@omni/data-services/dynamics-client/dynamics-client.service';
import { APPOINTMENT_REF_RESOLVER } from '@omni/services/meeting/appointment-ref-resolver';
import { awaitsItsWords, NOTE_COLLECTION, escapeODataLiteral, toNotePayload } from './note-payload';
import { buildNoteFetchXml, isServerId, rowToRawNote } from './note-fetch';
import { placeReadWords, ReadWords } from './transcript-segments';

/** Everything the capture modal knows when the rep hits Save. */
export interface VoiceNoteCaptureResult {
  transcript: string;
  type: VoiceNoteType;
  /** Detected note title; falls back to the transcript's first line when absent. */
  subject?: string;
  /** Full structured detection for the review step. */
  detected?: DetectedMentions;
  /** True when detection BROKE (timeout, transport, bad reply) rather than found
   *  nothing — consumers tell the rep why the review step will not appear. */
  detectionFailed?: boolean;
  /**
   * What the rep said, kept as audio because recognition was out of reach. The
   * caller stores it against the note it creates, since a recording is only
   * findable by the note it belongs to.
   */
  audio?: CapturedAudio;
}

/**
 * Voice-note store (OMNI-61023), synced against the `indskr_note` table.
 * fetchData pulls the rep's own active notes down into PouchDB; pushData
 * sends local creates, edits, and discards back up, resolving each row's
 * server id by external id before writing so a retry never duplicates it.
 */
@Injectable({ providedIn: 'root' })
export class VoiceNoteService extends OfflineWritableServiceBase<RawVoiceNote, 'indskr_iovoicenoteid'> {
  protected readonly offlineIdPrefix = 'offline_voicenote';
  protected override readonly offlineDataCountKey = OFFLINE_DATA_COUNT_ENTITY_NAME.VOICE_NOTES;

  readonly #auth = inject(AuthenticationService);
  readonly #selectedNote$ = new BehaviorSubject<VoiceNote | null>(null);

  /** The id Dynamics knows a filed note's meeting by; null while it is device-only. */
  private readonly resolveAppointmentRef = inject(APPOINTMENT_REF_RESOLVER);

  /**
   * Live notes for the My Notes UI, newest first. Excludes local tombstones and
   * notes whose record has been deactivated — which is what takes a converted
   * note off the list, since markConverted sets `statecode: 1`.
   */
  public readonly notes$: Observable<VoiceNote[]> = this.raw$.pipe(
    map(rows => rows
      .filter(row => !row.isDeleted)
      .map(row => new VoiceNote(row))
      .filter(note => note.isActive)
      .sort((a, b) => b.noteDate.getTime() - a.noteDate.getTime())),
  );

  /**
   * Every note still waiting against one meeting. `notes$` already drops the ones
   * that have been converted, so what is left here is what the rep has dictated
   * about that call and not yet reviewed.
   */
  public async notesAwaitingMeeting(meetingId: string): Promise<VoiceNote[]> {
    const id = (meetingId ?? '').trim();
    if (!id) {
      return [];
    }
    const notes = await firstValueFrom(this.notes$.pipe(take(1)));
    return notes.filter(note => note.targetMeetingId === id);
  }

  /**
   * Pulls the rep's active notes filed against a meeting from Dynamics into the
   * device store, so notes dictated elsewhere since the last sync are there when
   * the meeting is opened. The caller decides whether the device is online.
   *
   * @param meetingId The meeting's Dynamics record id; a meeting that exists only on the device has no notes to pull.
   * @returns Whether notes were fetched and stored.
   */
  public async refreshNotesForMeeting(meetingId: string): Promise<boolean> {
    if (!isServerId(meetingId) || !this.isAvailable()) {
      return false;
    }
    const rows = await this.dynamics.executeFetchQuery<Record<string, any>>(
      NOTE_COLLECTION,
      buildNoteFetchXml(null, meetingId.trim()),
    );
    return this.storePulledRows((rows ?? []).map(rowToRawNote));
  }

  /** Note selected in the My Notes list, shown in the right-pane detail. */
  public readonly selectedNote$: Observable<VoiceNote | null> = this.#selectedNote$.asObservable();

  constructor(
    disk: DiskService,
    deltaService: DeltaService,
    private readonly dynamics: DynamicsClientService,
    dateTimeFormatsService: DateTimeFormatsService,
    private readonly recordings: RecordingStore,
  ) {
    super(
      disk,
      deltaService,
      EntityNames.voiceNote,
      'indskr_iovoicenoteid',
      DB_KEY_PREFIXES.VOICE_NOTES,
      dateTimeFormatsService,
    );
  }

  public isAvailable(): boolean {
    return this.#auth.hasFeatureAction(FeatureActionsMap.VOICE_NOTE_TAKER);
  }

  public setSelectedNote(note: VoiceNote | null): void {
    this.#selectedNote$.next(note);
  }

  #pendingOpenNote: VoiceNote | null = null;

  /** Queue a note to auto-open in the My Notes detail pane the next time the list
   *  mounts — used when Save for Later navigates to My Notes from another page. */
  public queueNoteToOpen(note: VoiceNote): void {
    this.#pendingOpenNote = note;
  }

  /** Returns the queued note once, then clears it. */
  public takePendingOpenNote(): VoiceNote | null {
    const note = this.#pendingOpenNote;
    this.#pendingOpenNote = null;
    return note;
  }

  #pendingQuickCapture = false;

  /** Queue a new capture to auto-start the next time the My Notes list mounts —
   *  used when a deep link (e.g. a Siri shortcut) opens the app into dictation. */
  public queueQuickCapture(): void {
    this.#pendingQuickCapture = true;
  }

  /** Returns whether a capture was queued, then clears the flag. */
  public takePendingQuickCapture(): boolean {
    const pending = this.#pendingQuickCapture;
    this.#pendingQuickCapture = false;
    return pending;
  }

  /** Persists a finished capture as a draft note. */
  public async saveCapture(capture: VoiceNoteCaptureResult): Promise<VoiceNote> {
    const now = new Date();
    const raw = await this.create({
      indskr_name: capture.subject?.trim() || deriveNoteTitle(capture.transcript, now),
      indskr_transcript: capture.transcript,
      indskr_notedate: now.toISOString(),
      indskr_notetype: capture.type,
    });
    const note = new VoiceNote(raw);
    // A recording is only findable by the note it belongs to, so it is held once
    // that note has an id. The capture screen only records where the store said it
    // could hold one, so reaching here without somewhere to put it is a real fault
    // and is reported rather than leaving a note whose audio silently went nowhere.
    if (capture.audio) {
      await this.recordings.put({
        noteId: note.id,
        audio: capture.audio.audio,
        mimeType: capture.audio.mimeType,
        recordedAt: now.getTime(),
        seconds: capture.audio.seconds,
        partLengths: capture.audio.partLengths,
        partTypes: capture.audio.partTypes,
        sections: capture.audio.sections,
      });
      // The words are not here yet, and the recording is filed under this note's
      // id. Sending it now would settle it under the id Dynamics gives back and
      // leave the audio filed under an id that no longer names anything.
      return note;
    }
    return this.sendOnNow(raw, note);
  }

  /**
   * Sends a finished note to Dynamics now rather than leaving it for the next
   * sync, and answers with the note as it stands afterwards.
   *
   * A note the rep has finished belongs in Dynamics immediately; waiting for a
   * sync cycle leaves it looking saved on the device and absent everywhere else.
   * Settling moves the record to the id Dynamics gave it, so the note returned
   * here carries that id and not the device one. Offline, or where the push
   * fails, the note stays pending and reaches Dynamics on the next sync — the
   * same route a recording made out of range already takes.
   */
  private async sendOnNow(raw: RawVoiceNote, note: VoiceNote): Promise<VoiceNote> {
    let settled: PushOutcome[] = [];
    try {
      settled = await this.pushOfflineData();
    } catch (error) {
      console.error('VoiceNoteService: sending the note on failed; it stays pending', error);
      return note;
    }
    const sent = settled.find(outcome => outcome.localId === raw.indskr_iovoicenoteid);
    return sent
      ? new VoiceNote({ ...raw, indskr_iovoicenoteid: sent.serverId })
      : note;
  }

  /**
   * Put the words of a recording onto the note it was made for, and let go of the
   * audio. The recording has served its purpose once it has been read; keeping it
   * would leave a copy of the conversation on the device with nothing left to do.
   * The words go after anything already on the note.
   */
  public applyTranscript(id: string, transcript: string): Promise<VoiceNote> {
    return this.applyReadings(id, [{ words: transcript, before: null }]);
  }

  /**
   * Put the words read from each part of a recording onto its note, each where it
   * was said, let go of the audio, and send the note to Dynamics now, as saving
   * does. Words that followed a stretch of the note go after that stretch where the
   * note still begins with it; the rest, and words with no place, go after anything
   * already on the note, which may be the rep's own, typed before the recording was
   * read. Answers with the note as it stands afterwards, under the id Dynamics gave
   * it once it has been sent.
   */
  public async applyReadings(id: string, readings: ReadWords[]): Promise<VoiceNote> {
    const existing = (await firstValueFrom(this.notes$.pipe(take(1))))
      .find(note => note.id === id)?.transcript?.trim() ?? '';
    const raw = await this.update(id, { indskr_transcript: placeReadWords(existing, readings) });
    await this.recordings.remove(id);
    const note = await this.sendOnNow(raw, new VoiceNote(raw));
    // The rep is most likely still looking at the note they just recorded, and the
    // list updating underneath them does not reach the pane they are reading.
    if (this.#selectedNote$.value?.id === id) {
      this.#selectedNote$.next(note);
    }
    return note;
  }

  /** Files the note against the meeting it became and retires it. */
  public async markConverted(id: string, meetingId: string): Promise<VoiceNote> {
    const raw = await this.update(id, {
      indskr_convertedactivityid: meetingId,
    });
    return new VoiceNote(raw);
  }

  /**
   * Names the customer a note is about.
   *
   * **The phone is allowed to arrive with nobody.** Attaching at capture needs a
   * lookup the rep may have no signal for, and holding them there costs the note
   * — so an unattached note is a fallback the phone is entitled to take, and this
   * is where the rep puts it right. The choice is sent to Dynamics now, as saving
   * does; offline, or where the push fails, it stays pending for the next sync.
   * Answers with the note under the id Dynamics gave it once it has been sent.
   */
  public async attachCustomer(
    id: string,
    customer: { id: string; name: string; type: CustomerType },
  ): Promise<VoiceNote> {
    const raw = await this.update(id, {
      customerId: customer.id,
      customerName: customer.name,
      customerType: customer.type,
    });
    return this.sendOnNow(raw, new VoiceNote(raw));
  }

  /**
   * Deletes a note from the device and sends the deletion to Dynamics now, as
   * saving does, rather than leaving it for the next sync. Offline, or where the
   * push fails, the deletion stays pending and reaches Dynamics on the next sync.
   */
  public async discard(id: string): Promise<void> {
    await this.removeLocal(id);
    // The recording goes with the note. It is reachable only through that note,
    // so leaving it behind keeps a recording of the conversation on the device
    // that nothing can reach and nothing will ever read.
    await this.recordings.remove(id);
    try {
      await this.pushOfflineData();
    } catch (error) {
      console.error('VoiceNoteService: sending the deletion failed; it stays pending', error);
    }
  }

  /**
   * The ids of every note held on this device, read straight from disk.
   *
   * A read that cannot be completed raises rather than answering with an empty
   * list: anything deciding what to delete has to tell a device with no notes
   * apart from a store it could not read.
   */
  public async storedNoteIds(): Promise<string[]> {
    const rows = await this.disk.find<RawVoiceNote>({
      selector: {
        _id: {
          $gte: `${DB_KEY_PREFIXES.VOICE_NOTES}_`,
          $lte: `${DB_KEY_PREFIXES.VOICE_NOTES}_${PREFIX_SEARCH_ENDKEY_UNICODE}`,
        },
      },
    });
    return (rows ?? []).map((row) => row[this.idAttribute] as unknown as string).filter(Boolean);
  }

  protected async fetchData(lastUpdatedTime: string): Promise<RawVoiceNote[]> {
    const rows = await this.dynamics.executeFetchQuery<Record<string, any>>(
      NOTE_COLLECTION,
      buildNoteFetchXml(lastUpdatedTime),
    );
    return (rows ?? []).map(rowToRawNote);
  }

  protected async pushData(pending: RawVoiceNote[]): Promise<PushOutcome[]> {
    const outcomes: PushOutcome[] = [];
    for (const raw of pending) {
      const localId = raw.indskr_iovoicenoteid;
      try {
        if (raw.isDeleted) {
          const serverId = await this.resolveServerId(raw);
          if (serverId) {
            await this.dynamics.delete(serverId, NOTE_COLLECTION);
            outcomes.push({ localId, serverId });
          } else {
            outcomes.push({ localId, serverId: localId });
          }
          continue;
        }
        // An insight is not a meeting note and has no honest value for
        // indskr_type, so it lives out its life on the device. It still
        // reports settled so it drops off the pending-upload count instead
        // of sitting in it forever.
        if (raw.indskr_notetype === VoiceNoteType.Insight) {
          outcomes.push({ localId, serverId: localId });
          continue;
        }
        const targetRef = raw.indskr_convertedactivityid ?? raw.targetMeetingId;
        const targetAppointmentId = targetRef ? this.resolveAppointmentRef(targetRef) : null;
        if (targetRef && !targetAppointmentId) {
          // The meeting this note was filed against has not reached Dynamics yet,
          // so there is no id to bind to. Hold the note back rather than pushing
          // it unlinked: omitting an outcome leaves it pending, and the cycle
          // after the meeting upload finds the real id and sends it.
          console.warn(`VoiceNoteService: deferring ${localId} — its meeting has not synced yet`);
          continue;
        }
        if (awaitsItsWords(raw)) {
          continue;
        }
        const payload = toNotePayload(raw, targetAppointmentId);
        const existing = await this.resolveServerId(raw);
        if (existing) {
          await this.dynamics.update(existing, NOTE_COLLECTION, payload);
          outcomes.push({ localId, serverId: existing });
        } else {
          const created = await this.dynamics.create<{ indskr_noteid: string }>(payload, NOTE_COLLECTION);
          outcomes.push({ localId, serverId: created['indskr_noteid'] });
        }
      } catch (error) {
        // The record stays pending and the next cycle tries again.
        console.error(`VoiceNoteService: push failed for ${localId}`, error);
      }
    }
    return outcomes;
  }

  /**
   * The row's server id. A record that already carries a GUID is its own answer;
   * a device-created one is found by external id, which is how a retry after an
   * ambiguous failure avoids writing the note twice.
   */
  private async resolveServerId(raw: RawVoiceNote): Promise<string | null> {
    if (!raw.indskr_iovoicenoteid.startsWith(this.offlineIdPrefix)) {
      return raw.indskr_iovoicenoteid;
    }
    const externalId = raw.externalId ?? raw.indskr_iovoicenoteid;
    const response = await this.dynamics.retrieveAll<{ indskr_noteid: string }>(
      NOTE_COLLECTION,
      ['indskr_noteid'],
      `indskr_externalid eq '${escapeODataLiteral(externalId)}'`,
    );
    return response?.value?.[0]?.indskr_noteid ?? null;
  }

  protected override isSyncAllowed(): boolean {
    return this.isAvailable();
  }
}
